import { isServer } from '@moonpig/web-core-utils'
import { createMetrics, logger } from '@moonpig/web-core-monitoring'
import { EppoExperimentOverrides } from '@moonpig/web-core-utils/src/eppoContext/EppoContext'
import { Flag, VariationType } from '@eppo/js-client-sdk-common'
import { DefaultSubjectAttributes } from '../../shared/DefaultSubjectAttributes'
import { EppoConfiguration } from '../../types'
import { setupGetAllExperimentVariants } from '../setupGetAllExperimentVariants/setupGetAllExperimentVariants'
import {
  GetExperiment,
  setupGetExperimentVariant,
} from '../setupGetExperimentVariant/setupGetExperimentVariant'
import { createEppoClient } from '../../browser'

type SetupEppo = {
  sessionId: string
  defaultAttributes: DefaultSubjectAttributes
  overrides: EppoExperimentOverrides
}

const EPPO_REFRESH_INTERVAL = 30000
const EPPO_TIMEOUT = 1000
let eppoConfiguration: EppoConfiguration

// The contentfulConfiguration only exists to query the experiments tagged with `Contentful`.
// This seems the only reliable way of understanding which experiments to pass through
// automatically to the content-api. It is not used outside of `getContentfulExperimentNames`.
//
// A query has been raised with Eppo around being able to see experiment tags without needing to do
// the above.
let contentfulConfiguration: EppoConfiguration
let contentfulExperimentNames: string[]
let lastFetch: Date

export const setupEppo = async ({
  sessionId,
  defaultAttributes,
  overrides,
}: SetupEppo) => {
  if (isServer) {
    const shouldFetch = lastFetch
      ? new Date().getTime() - lastFetch.getTime() >= EPPO_REFRESH_INTERVAL
      : true

    if (shouldFetch) {
      ;[eppoConfiguration, contentfulConfiguration] = await Promise.all([
        fetchEppoConfiguration(process.env.EPPO_API_SDK_KEY),
        fetchEppoConfiguration(process.env.EPPO_CONTENTFUL_API_SDK_KEY),
      ])
    }
  }

  const client = createEppoClient({
    flagsConfiguration: eppoConfiguration,
  })

  const {
    getExperimentString,
    getExperimentBoolean,
    getExperimentJson,
    getExperimentInteger,
    getExperimentNumeric,
    trackedServerExperiments,
  } = setupGetExperimentVariant({
    client,
    subjectKey: sessionId,
    defaultAttributes,
    overrides,
  })

  const getAllExperimentVariants = setupGetAllExperimentVariants({
    client,
    subjectKey: sessionId,
    defaultAttributes,
    overrides,
  })

  const getContentfulExperimentNames = () => {
    if (!contentfulExperimentNames) {
      contentfulExperimentNames = Object.values(
        contentfulConfiguration ?? {},
      ).reduce(
        (acc, flag) => (flag.enabled ? [...acc, flag.key] : acc),
        [] as string[],
      )
    }

    return contentfulExperimentNames
  }

  const getExperiment: GetExperiment = (experimentKey, args) => {
    if (!eppoConfiguration) return args.fallback
    const config = eppoConfiguration[experimentKey] as Flag
    if (!config) return args.fallback

    switch (config.variationType) {
      case VariationType.STRING:
        return getExperimentString(experimentKey, args)
      case VariationType.BOOLEAN:
        return getExperimentBoolean(experimentKey, args)
      case VariationType.JSON:
        return getExperimentJson(experimentKey, args)
      case VariationType.INTEGER:
        return getExperimentInteger(experimentKey, args)
      case VariationType.NUMERIC:
        return getExperimentNumeric(experimentKey, args)
      default:
        logger.fixToday(
          `Unknown variation type "${config.variationType}" for experiment "${experimentKey}"`,
        )
        return args.fallback
    }
  }

  return {
    eppoConfiguration,
    getExperiment,
    getContentfulExperimentNames,
    getAllExperimentVariants,
    trackedServerExperiments,
  }
}

const fetchEppoConfiguration = async (apiKey: string | undefined) => {
  const eppoUrl = new URL('https://fscdn.eppo.cloud/api/flag-config/v1/config')
  eppoUrl.search = new URLSearchParams({
    apiKey: `${apiKey}`,
    sdkName: 'node-server-sdk',
    sdkVersion: '3.5.0',
  }).toString()

  const abortController = new AbortController()
  const timerId = setTimeout(() => {
    logger.fixToday(`EPPO reached timeout of ${EPPO_TIMEOUT}ms`)
    abortController.abort()
  }, EPPO_TIMEOUT)
  const { traceAsync, count } = createMetrics({ useTracing: false })

  try {
    const res = await traceAsync(
      { metricName: 'fetch-eppo-configuration' },
      async () => fetch(eppoUrl, { signal: abortController.signal }),
    )

    if (!res.ok) {
      throw new Error(`Failed to fetch EPPO configuration: ${res.statusText}`)
    }

    const json = await res.json()

    if (!json.flags) {
      throw new Error('Failed to fetch EPPO configuration: no flags found')
    }

    lastFetch = new Date()
    return json.flags
  } catch (error) {
    logger.fixToday('Failed to fetch EPPO configuration', error)
    if (!eppoConfiguration) {
      count({ metricName: 'empty-eppo-configuration' })
      return {}
    }
    return eppoConfiguration
  } finally {
    clearTimeout(timerId)
  }
}
